/**
 * 五子棋棋盘类
 * 实现基础的棋盘操作和游戏规则
 */
export class Board {
    constructor(size = 15) {
        this.size = size;
        this.board = Array(size).fill(null).map(() => Array(size).fill(0));
        this.currentPlayer = 1; // 1: 黑棋, 2: 白棋
        this.lastMove = null;
        this.moveHistory = [];
        this.gameOver = false;
        this.winner = null;
        this.directions = [
            [0, 1],   // 水平
            [1, 0],   // 垂直
            [1, 1],   // 主对角线
            [1, -1]   // 副对角线
        ];
    }

    /**
     * 落子
     * @param {number} row 行
     * @param {number} col 列
     * @returns {boolean} 是否成功落子
     */
    makeMove(row, col) {
        if (!this.isValidMove(row, col) || this.gameOver) {
            return false;
        }

        this.board[row][col] = this.currentPlayer;
        this.lastMove = { row, col, player: this.currentPlayer };
        this.moveHistory.push({ ...this.lastMove });

        // 检查是否获胜
        if (this.checkWin(row, col)) {
            this.gameOver = true;
            this.winner = this.currentPlayer;
        } else if (this.isBoardFull()) {
            this.gameOver = true;
            this.winner = 0; // 平局
        } else {
            this.currentPlayer = 3 - this.currentPlayer;
        }

        return true;
    }

    /**
     * 撤销上一步
     * @returns {boolean} 是否成功撤销
     */
    undoMove() {
        if (this.moveHistory.length === 0) {
            return false;
        }

        const lastMove = this.moveHistory.pop();
        this.board[lastMove.row][lastMove.col] = 0;
        this.currentPlayer = lastMove.player;
        this.lastMove = this.moveHistory.length > 0 ? this.moveHistory[this.moveHistory.length - 1] : null;
        this.gameOver = false;
        this.winner = null;

        return true;
    }

    /**
     * 检查位置是否有效
     * @param {number} row 行
     * @param {number} col 列
     * @returns {boolean} 是否有效
     */
    isValidMove(row, col) {
        return row >= 0 && row < this.size && col >= 0 && col < this.size && this.board[row][col] === 0;
    }

    /**
     * 检查是否获胜
     * @param {number} row 最后落子的行
     * @param {number} col 最后落子的列
     * @returns {boolean} 是否获胜
     */
    checkWin(row, col) {
        const player = this.board[row][col];

        for (const [dx, dy] of this.directions) {
            let count = 1;

            // 正方向检查
            for (let i = 1; i < 5; i++) {
                const newRow = row + i * dx;
                const newCol = col + i * dy;
                if (newRow >= 0 && newRow < this.size && newCol >= 0 && newCol < this.size &&
                    this.board[newRow][newCol] === player) {
                    count++;
                } else {
                    break;
                }
            }

            // 反方向检查
            for (let i = 1; i < 5; i++) {
                const newRow = row - i * dx;
                const newCol = col - i * dy;
                if (newRow >= 0 && newRow < this.size && newCol >= 0 && newCol < this.size &&
                    this.board[newRow][newCol] === player) {
                    count++;
                } else {
                    break;
                }
            }

            if (count >= 5) {
                return true;
            }
        }

        return false;
    }

    /**
     * 检查棋盘是否已满
     * @returns {boolean} 是否已满
     */
    isBoardFull() {
        for (let i = 0; i < this.size; i++) {
            for (let j = 0; j < this.size; j++) {
                if (this.board[i][j] === 0) {
                    return false;
                }
            }
        }
        return true;
    }

    /**
     * 检查游戏是否结束
     * @returns {boolean} 是否结束
     */
    isGameOver() {
        return this.gameOver;
    }

    /**
     * 获取获胜者
     * @returns {number} 获胜者（0表示平局）
     */
    getWinner() {
        return this.winner;
    }

    /**
     * 获取所有空位置
     * @returns {Array} 空位置数组
     */
    getEmptyPositions() {
        const positions = [];
        for (let i = 0; i < this.size; i++) {
            for (let j = 0; j < this.size; j++) {
                if (this.board[i][j] === 0) {
                    positions.push({ row: i, col: j });
                }
            }
        }
        return positions;
    }

    /**
     * 获取指定位置周围的有效位置
     * @param {number} row 行
     * @param {number} col 列
     * @param {number} radius 半径
     * @returns {Array} 周围位置数组
     */
    getNearbyPositions(row, col, radius = 2) {
        const positions = [];
        for (let i = Math.max(0, row - radius); i <= Math.min(this.size - 1, row + radius); i++) {
            for (let j = Math.max(0, col - radius); j <= Math.min(this.size - 1, col + radius); j++) {
                if (this.board[i][j] === 0) {
                    positions.push({ row: i, col: j });
                }
            }
        }
        return positions;
    }

    /**
     * 获取所有相关的空位置（有棋子周围的位置）
     * @returns {Array} 相关位置数组
     */
    getRelevantEmptyPositions() {
        const relevantPositions = new Set();

        for (let i = 0; i < this.size; i++) {
            for (let j = 0; j < this.size; j++) {
                if (this.board[i][j] !== 0) {
                    const nearby = this.getNearbyPositions(i, j, 2);
                    nearby.forEach(pos => {
                        relevantPositions.add(`${pos.row},${pos.col}`);
                    });
                }
            }
        }

        // 如果棋盘为空，返回中心位置
        if (relevantPositions.size === 0) {
            const center = Math.floor(this.size / 2);
            return [{ row: center, col: center }];
        }

        return Array.from(relevantPositions).map(pos => {
            const [row, col] = pos.split(',').map(Number);
            return { row, col };
        });
    }

    /**
     * 重置棋盘
     */
    reset() {
        this.board = Array(this.size).fill(null).map(() => Array(this.size).fill(0));
        this.currentPlayer = 1;
        this.lastMove = null;
        this.moveHistory = [];
        this.gameOver = false;
        this.winner = null;
    }

    /**
     * 复制棋盘
     * @returns {Board} 复制的棋盘
     */
    clone() {
        const newBoard = new Board(this.size);
        newBoard.board = this.board.map(row => [...row]);
        newBoard.currentPlayer = this.currentPlayer;
        newBoard.lastMove = this.lastMove ? { ...this.lastMove } : null;
        newBoard.moveHistory = [...this.moveHistory];
        newBoard.gameOver = this.gameOver;
        newBoard.winner = this.winner;
        return newBoard;
    }
}